import { createAsyncThunk } from '@reduxjs/toolkit';
import { CareOrder, type SizeModeEnum } from '@talos/kyoko';
import { v4 as uuid } from 'uuid';
import { rfqSliceActions } from '../../../../components/OMS/NewRFQ/RFQSlice';
import { careOrderToPrimeRFQParams } from '../../../../components/OMS/NewRFQ/utils';
import { openView } from '../../../../components/OMS/OMSSlice';
import { OMSView } from '../../../../components/OMS/OMSView';
import type { AppExtraArgument, AppState } from '../../../../providers/AppStateProvider/types';
import { baseRestApi } from '../../../../utils/rtkQuery';

interface SubmitCareOrderNettingParams {
  memberCareOrderIDs: string[];
  orderID?: string;
  primeRFQ?: boolean;
  sizeMode: SizeModeEnum;
  subAccount?: string;
}

interface SubmitCareOrderNettingRequest {
  clOrdID: string;
  memberCareOrderIDs: string[];
  orderID?: string;
  sizeMode: SizeModeEnum;
  subAccount?: string;
}

const submitCareOrderNettingApi = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    submitCareOrderNetting: builder.mutation<CareOrder[], SubmitCareOrderNettingRequest>({
      query: ({ clOrdID, orderID, memberCareOrderIDs, sizeMode, subAccount }) => ({
        method: orderID == null ? 'POST' : 'PATCH',
        url: '/care/working/orders',
        headers: { 'Content-Type': 'application/json' },
        body: {
          ClOrdId: clOrdID,
          OrderID: orderID,
          MemberCareOrders: memberCareOrderIDs,
          SizeMode: sizeMode,
          ...(orderID == null && subAccount != null ? { SubAccount: subAccount } : {}),
        },
      }),
    }),
  }),
});

/**
 * Applies a care-order netting create/modify request.
 *
 * The backend returns the resulting WorkingOrder in the REST DataResponse. `baseRestApi` unwraps that response's
 * `data` array and supplies the org API endpoint from Redux state.
 */
export const submitCareOrderNettingThunk = createAsyncThunk<
  CareOrder | undefined,
  SubmitCareOrderNettingParams,
  { state: AppState; extra: AppExtraArgument }
>('careOrderNettingPreview/submitCareOrderNettingThunk', async (params, thunk) => {
  const clOrdID = uuid();
  const response = await thunk
    .dispatch(
      submitCareOrderNettingApi.endpoints.submitCareOrderNetting.initiate({
        clOrdID,
        memberCareOrderIDs: params.memberCareOrderIDs,
        orderID: params.orderID,
        sizeMode: params.sizeMode,
        subAccount: params.subAccount,
      })
    )
    .unwrap();
  const workingOrder = response.at(0) == null ? undefined : new CareOrder(response[0]);

  if (params.primeRFQ) {
    if (workingOrder == null) {
      throw new Error('Working order response is missing.');
    }

    // Prime from the persisted WorkingOrder rather than the preview; the backend may normalize fields during submit.
    thunk.dispatch(openView(OMSView.RFQForm));
    thunk.dispatch(rfqSliceActions.primeNewRFQForm(careOrderToPrimeRFQParams(workingOrder)));
  }

  return workingOrder;
});
